import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Bell, Calendar, Clock, Flag, Zap } from 'lucide-react';
import {
  useReminders,
  Reminder,
  ReminderCategory,
  ReminderPriority,
  RepeatOption,
} from '../../context/RemindersContext';
import { usePets } from '../../context/PetsContext';
import { useNotifications } from '../../context/NotificationsContext';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { CalendarPicker } from '../ui/calendar-picker';
import { toast } from 'sonner';

interface CreateReminderModalProps {
  onClose: () => void;
  mode: 'petso' | 'petso-pro';
  defaultPetId?: string;
  defaultAnimalId?: string;
  defaultDate?: string;
  defaultTime?: string;
  /** When provided, modal is in edit mode and will call updateReminder on submit */
  editingReminder?: Reminder | null;
}

export function CreateReminderModal({
  onClose,
  mode,
  defaultPetId,
  defaultAnimalId,
  defaultDate,
  defaultTime,
  editingReminder = null,
}: CreateReminderModalProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const { colors, theme } = useTheme();
  const { addReminder, updateReminder } = useReminders();
  const { pets } = usePets();
  const { addNotification } = useNotifications();

  const isEditing = !!editingReminder;

  const [formData, setFormData] = useState(() => {
    if (editingReminder) {
      return {
        title: editingReminder.title,
        category: editingReminder.category,
        petId: editingReminder.petId || '',
        animalId: editingReminder.animalId || '',
        clientId: editingReminder.clientId || '',
        date: editingReminder.date,
        time: editingReminder.time,
        repeat: editingReminder.repeat,
        customRepeatDays: editingReminder.customRepeatDays ?? 1,
        priority: editingReminder.priority,
        notes: editingReminder.notes || '',
        isInternal: editingReminder.isInternal ?? false,
        notifications: editingReminder.notifications ?? {
          enabled: true,
          channels: ['in-app' as const, 'email' as const],
          timing: ['1-day-before' as const, 'at-time' as const],
        },
      };
    }
    return {
      title: '',
      category: 'custom' as ReminderCategory,
      petId: defaultPetId || '',
      animalId: defaultAnimalId || '',
      clientId: '',
      date: defaultDate || new Date().toISOString().split('T')[0],
      time: defaultTime || '09:00',
      repeat: 'one-time' as RepeatOption,
      customRepeatDays: 1,
      priority: 'normal' as ReminderPriority,
      notes: '',
      isInternal: false,
      notifications: {
        enabled: true,
        channels: ['in-app' as const, 'email' as const],
        timing: ['1-day-before' as const, 'at-time' as const],
      },
    };
  });

  const [showCalendar, setShowCalendar] = useState(false);

  const categoryOptions: {
    value: ReminderCategory;
    labelKey: string;
    icon: string;
  }[] = [
    {
      value: 'vaccination',
      labelKey: 'calendar.categoryVaccination',
      icon: '💉',
    },
    {
      value: 'health-checkup',
      labelKey: 'calendar.categoryHealthCheckup',
      icon: '🏥',
    },
    {
      value: 'medication',
      labelKey: 'calendar.categoryMedication',
      icon: '💊',
    },
    { value: 'grooming', labelKey: 'calendar.categoryGrooming', icon: '✂️' },
    { value: 'follow-up', labelKey: 'calendar.categoryFollowUp', icon: '📋' },
    {
      value: 'document-renewal',
      labelKey: 'calendar.categoryDocumentRenewal',
      icon: '📄',
    },
    { value: 'custom', labelKey: 'calendar.categoryCustom', icon: '📌' },
  ];

  const priorityOptions: {
    value: ReminderPriority;
    labelKey: string;
    color: string;
  }[] = [
    {
      value: 'normal',
      labelKey: 'calendar.createReminderModal.priorityNormal',
      color: '#6B7280',
    },
    {
      value: 'important',
      labelKey: 'calendar.createReminderModal.priorityImportant',
      color: '#F59E0B',
    },
    {
      value: 'critical',
      labelKey: 'calendar.createReminderModal.priorityCritical',
      color: '#EF4444',
    },
  ];

  const repeatOptions: { value: RepeatOption; labelKey: string }[] = [
    {
      value: 'one-time',
      labelKey: 'calendar.createReminderModal.repeatOneTime',
    },
    { value: 'daily', labelKey: 'calendar.createReminderModal.repeatDaily' },
    { value: 'weekly', labelKey: 'calendar.createReminderModal.repeatWeekly' },
    {
      value: 'monthly',
      labelKey: 'calendar.createReminderModal.repeatMonthly',
    },
    { value: 'custom', labelKey: 'calendar.createReminderModal.repeatCustom' },
  ];

  // Generate time options (every 30 minutes)
  const timeOptions = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const hourStr = hour.toString().padStart(2, '0');
      const minuteStr = minute.toString().padStart(2, '0');
      const timeValue = `${hourStr}:${minuteStr}`;
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const displayTime = `${displayHour}:${minuteStr} ${period}`;
      timeOptions.push({ value: timeValue, label: displayTime });
    }
  }

  // Format date for display (use current locale)
  const formatDateDisplay = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString(locale, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title) {
      toast.error(t('calendar.createReminderModal.toastTitleRequired'));
      return;
    }

    if (mode === 'petso' && !formData.petId) {
      toast.error(t('calendar.createReminderModal.toastSelectPet'));
      return;
    }

    const petName =
      mode === 'petso' && formData.petId
        ? (pets.find((p) => p.id === formData.petId)?.name ??
          t('calendar.createReminderModal.yourPet'))
        : undefined;

    if (isEditing && editingReminder) {
      updateReminder(editingReminder.id, {
        title: formData.title,
        category: formData.category,
        petId: mode === 'petso' ? formData.petId || undefined : undefined,
        animalId:
          mode === 'petso-pro' ? formData.animalId || undefined : undefined,
        clientId:
          mode === 'petso-pro' ? formData.clientId || undefined : undefined,
        date: formData.date,
        time: formData.time,
        repeat: formData.repeat,
        customRepeatDays:
          formData.repeat === 'custom' ? formData.customRepeatDays : undefined,
        priority: formData.priority,
        notes: formData.notes,
        isInternal: mode === 'petso-pro' ? formData.isInternal : undefined,
        notifications: formData.notifications,
      });
      addNotification({
        type: 'reminder',
        title: t(
          'calendar.createReminderModal.reminderUpdatedNotificationTitle'
        ),
        message: t(
          'calendar.createReminderModal.reminderUpdatedNotificationMessage',
          {
            title: formData.title,
          }
        ),
        petId: mode === 'petso' ? formData.petId : undefined,
        petName,
        icon: '⏰',
        source: t('calendar.title'),
      });
      toast.success(t('calendar.createReminderModal.toastUpdated'));
    } else {
      addReminder({
        title: formData.title,
        category: formData.category,
        petId: mode === 'petso' ? formData.petId : undefined,
        animalId: mode === 'petso-pro' ? formData.animalId : undefined,
        clientId: mode === 'petso-pro' ? formData.clientId : undefined,
        date: formData.date,
        time: formData.time,
        repeat: formData.repeat,
        customRepeatDays:
          formData.repeat === 'custom' ? formData.customRepeatDays : undefined,
        priority: formData.priority,
        status: 'upcoming',
        notes: formData.notes,
        createdBy: 'current-user',
        isInternal: mode === 'petso-pro' ? formData.isInternal : undefined,
        notifications: formData.notifications,
      });
      addNotification({
        type: 'reminder',
        title: t(
          'calendar.createReminderModal.reminderCreatedNotificationTitle'
        ),
        message: t(
          'calendar.createReminderModal.reminderCreatedNotificationMessage',
          {
            title: formData.title,
            target: petName ?? t('calendar.createReminderModal.yourCalendar'),
          }
        ),
        petId: mode === 'petso' ? formData.petId : undefined,
        petName,
        icon: '⏰',
        source: t('calendar.title'),
      });
      toast.success(t('calendar.createReminderModal.toastCreated'));
    }
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: colors.cardBg }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div
              className="p-3 rounded-xl"
              style={{
                backgroundColor:
                  theme === 'dark' ? 'rgba(99, 102, 241, 0.15)' : '#EEF2FF',
              }}
            >
              <Bell
                className="w-6 h-6"
                style={{ color: theme === 'dark' ? '#A5B4FC' : '#6366F1' }}
              />
            </div>
            <div>
              <h2
                style={{
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: 700,
                  fontSize: '24px',
                  color: colors.heading,
                }}
              >
                {isEditing
                  ? t('calendar.createReminderModal.editReminder')
                  : t('calendar.createReminderModal.createReminder')}
              </h2>
              <p
                className="text-sm mt-1"
                style={{ color: colors.textSecondary }}
              >
                {isEditing
                  ? t('calendar.createReminderModal.subtitleEdit')
                  : t('calendar.createReminderModal.subtitleCreate')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-colors"
            style={{ backgroundColor: 'transparent' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.cardBgSecondary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <X className="w-5 h-5" style={{ color: colors.textSecondary }} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div>
            <Label>{t('calendar.createReminderModal.reminderTitle')}</Label>
            <Input
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              placeholder={t(
                'calendar.createReminderModal.reminderTitlePlaceholder'
              )}
              className="mt-2"
            />
          </div>

          {/* Category */}
          <div>
            <Label>{t('calendar.createReminderModal.category')}</Label>
            <Select
              value={formData.category}
              onValueChange={(value: ReminderCategory) =>
                setFormData({ ...formData, category: value })
              }
            >
              <SelectTrigger className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categoryOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <span className="flex items-center gap-2">
                      <span>{option.icon}</span>
                      <span>{t(option.labelKey)}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Pet Selection (Petso only) */}
          {mode === 'petso' && (
            <div>
              <Label>{t('calendar.createReminderModal.selectPet')}</Label>
              <Select
                value={formData.petId}
                onValueChange={(value) =>
                  setFormData({ ...formData, petId: value })
                }
              >
                <SelectTrigger className="mt-2">
                  <SelectValue
                    placeholder={t('calendar.createReminderModal.choosePet')}
                  />
                </SelectTrigger>
                <SelectContent>
                  {pets.map((pet) => (
                    <SelectItem key={pet.id} value={pet.id}>
                      {pet.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('calendar.createReminderModal.dateLabel')}</Label>
              <div className="relative mt-2">
                <button
                  type="button"
                  onClick={() => setShowCalendar(!showCalendar)}
                  className="w-full px-4 py-2 rounded-lg border text-left flex items-center gap-2 transition-colors"
                  style={{
                    backgroundColor: colors.cardBgSecondary,
                    borderColor: colors.border,
                    color: colors.text,
                  }}
                >
                  <Calendar
                    className="w-4 h-4"
                    style={{ color: colors.textSecondary }}
                  />
                  <span>{formatDateDisplay(formData.date)}</span>
                </button>
                {showCalendar && (
                  <CalendarPicker
                    value={formData.date}
                    onChange={(date) => {
                      setFormData({ ...formData, date });
                      setShowCalendar(false);
                    }}
                    onClose={() => setShowCalendar(false)}
                  />
                )}
              </div>
            </div>
            <div>
              <Label>{t('calendar.createReminderModal.timeLabel')}</Label>
              <div className="relative mt-2">
                <Clock
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 pointer-events-none z-10"
                  style={{ color: colors.textSecondary }}
                />
                <Select
                  value={formData.time}
                  onValueChange={(value) =>
                    setFormData({ ...formData, time: value })
                  }
                >
                  <SelectTrigger className="pl-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {timeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Repeat */}
          <div>
            <Label>{t('calendar.createReminderModal.repeat')}</Label>
            <Select
              value={formData.repeat}
              onValueChange={(value: RepeatOption) =>
                setFormData({ ...formData, repeat: value })
              }
            >
              <SelectTrigger className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {repeatOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {formData.repeat === 'custom' && (
              <div
                className="mt-3 p-4 rounded-xl"
                style={{ backgroundColor: colors.cardBgSecondary }}
              >
                <Label className="text-sm" style={{ color: colors.text }}>
                  {t('calendar.createReminderModal.repeatEvery')}
                </Label>
                <div className="flex items-center gap-2 mt-2">
                  <Input
                    type="number"
                    min="1"
                    value={formData.customRepeatDays}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        customRepeatDays: parseInt(e.target.value) || 1,
                      })
                    }
                    className="w-20"
                  />
                  <span
                    className="text-sm"
                    style={{ color: colors.textSecondary }}
                  >
                    {formData.customRepeatDays === 1
                      ? t('calendar.createReminderModal.day')
                      : t('calendar.createReminderModal.days')}
                  </span>
                </div>
                <p
                  className="text-xs mt-2"
                  style={{ color: colors.textSecondary }}
                >
                  {t('calendar.createReminderModal.repeatCustomHint', {
                    count: formData.customRepeatDays,
                    unit:
                      formData.customRepeatDays === 1
                        ? t('calendar.createReminderModal.day')
                        : t('calendar.createReminderModal.days'),
                  })}
                </p>
              </div>
            )}
          </div>

          {/* Priority */}
          <div>
            <Label>{t('calendar.createReminderModal.priority')}</Label>
            <div className="grid grid-cols-3 gap-3 mt-2">
              {priorityOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() =>
                    setFormData({
                      ...formData,
                      priority: option.value as ReminderPriority,
                    })
                  }
                  className="p-3 rounded-lg border-2 transition-all"
                  style={{
                    borderColor:
                      formData.priority === option.value
                        ? option.color
                        : colors.border,
                    backgroundColor:
                      formData.priority === option.value
                        ? theme === 'dark'
                          ? `${option.color}20`
                          : `${option.color}10`
                        : 'transparent',
                    color:
                      formData.priority === option.value
                        ? option.color
                        : colors.text,
                  }}
                >
                  <Flag className="w-4 h-4 mx-auto mb-1" />
                  <div className="text-sm">{t(option.labelKey)}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Petso Pro Only - Internal Reminder */}
          {mode === 'petso-pro' && (
            <div
              className="flex items-center justify-between p-4 rounded-xl"
              style={{ backgroundColor: colors.cardBgSecondary }}
            >
              <div className="flex items-center gap-3">
                <Zap
                  className="w-5 h-5"
                  style={{ color: colors.textSecondary }}
                />
                <div>
                  <div
                    style={{
                      fontFamily: 'Sora, sans-serif',
                      fontWeight: 600,
                      color: colors.heading,
                    }}
                  >
                    {t('calendar.createReminderModal.internalReminder')}
                  </div>
                  <p
                    className="text-sm"
                    style={{ color: colors.textSecondary }}
                  >
                    {t(
                      'calendar.createReminderModal.internalReminderDescription'
                    )}
                  </p>
                </div>
              </div>
              <Switch
                checked={formData.isInternal}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, isInternal: checked })
                }
              />
            </div>
          )}

          {/* Notifications */}
          <div>
            <Label>
              {t('calendar.createReminderModal.notificationSettings')}
            </Label>
            <div
              className="mt-2 p-4 rounded-xl space-y-3"
              style={{ backgroundColor: colors.cardBgSecondary }}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm" style={{ color: colors.text }}>
                  {t('calendar.createReminderModal.enableNotifications')}
                </span>
                <Switch
                  checked={formData.notifications.enabled}
                  onCheckedChange={(checked) =>
                    setFormData({
                      ...formData,
                      notifications: {
                        ...formData.notifications,
                        enabled: checked,
                      },
                    })
                  }
                />
              </div>
              {formData.notifications.enabled && (
                <>
                  <div
                    className="text-sm"
                    style={{ color: colors.textSecondary }}
                  >
                    {t('calendar.createReminderModal.notifyMe')}
                  </div>
                  <div className="space-y-2">
                    {[
                      {
                        value: 'at-time',
                        labelKey:
                          'calendar.createReminderModal.atTimeOfReminder',
                      },
                      {
                        value: '1-day-before',
                        labelKey: 'calendar.createReminderModal.oneDayBefore',
                      },
                      {
                        value: '3-days-before',
                        labelKey:
                          'calendar.createReminderModal.threeDaysBefore',
                      },
                    ].map((option) => (
                      <label
                        key={option.value}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.notifications.timing.includes(
                            option.value as
                              | 'at-time'
                              | '1-day-before'
                              | '3-days-before'
                          )}
                          onChange={(e) => {
                            const timing = e.target.checked
                              ? [
                                  ...formData.notifications.timing,
                                  option.value as
                                    | 'at-time'
                                    | '1-day-before'
                                    | '3-days-before',
                                ]
                              : formData.notifications.timing.filter(
                                  (t) => t !== option.value
                                );
                            setFormData({
                              ...formData,
                              notifications: {
                                ...formData.notifications,
                                timing,
                              },
                            });
                          }}
                          className="rounded"
                        />
                        <span
                          className="text-sm"
                          style={{ color: colors.text }}
                        >
                          {t(option.labelKey)}
                        </span>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label>{t('calendar.createReminderModal.notesOptional')}</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              placeholder={t('calendar.createReminderModal.notesPlaceholder')}
              className="mt-2"
              rows={3}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={onClose}
            >
              {t('calendar.createReminderModal.cancel')}
            </Button>
            <Button
              type="submit"
              className="flex-1"
              style={{ backgroundColor: '#6366F1' }}
            >
              {isEditing
                ? t('calendar.createReminderModal.saveChanges')
                : t('calendar.createReminderModal.createReminderButton')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
